import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { departmentService } from "../services/api";
import ConfirmModal from "../components/ConfirmModal";
import "./MasterScreen.css";

const DepartmentPage = () => {
  const { user } = useContext(AuthContext);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: "", shortName: "" });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (user?.role === "SuperAdmin") fetchDepartments();
  }, [user]);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentService.getAll();
      setDepartments(response.data || []);
      setError("");
    } catch (err) {
      setError("Failed to fetch department records from the secure server.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await departmentService.update(editingId, formData);
      } else {
        await departmentService.create(formData);
      }
      setFormData({ name: "", shortName: "" });
      setEditingId(null);
      setShowForm(false);
      fetchDepartments();
    } catch (err) {
      setError("System failure during data commitment.");
    }
  };

  const handleEdit = (dept) => {
    setFormData(dept);
    setEditingId(dept._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await departmentService.delete(deleteTargetId);
      setDeleteTargetId(null);
      fetchDepartments();
    } catch (err) {
      setError("Execution failed: dependency restriction.");
      setDeleteTargetId(null);
    }
  };

  const filteredDepartments = departments.filter(
    (dept) =>
      dept.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.shortName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="loading">Syncing Records...</div>;

  return (
    <div className="master-screen animated-bg">
      <div className="screen-content">
        <header className="screen-header-rail">
          <h1>Department Hub</h1>
          <button
            onClick={() => { setShowForm(!showForm); if (showForm) setEditingId(null); }}
            className="btn-primary"
          >
            {showForm ? "✕ Dismiss" : "＋ New Department"}
          </button>
        </header>

        {error && <div className="error-message">{error}</div>}

        {showForm && (
          <form className="master-form-glass" onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Official Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Information Technology"
                  required
                />
              </div>
              <div className="form-group">
                <label>Code Identifier</label>
                <input
                  type="text"
                  value={formData.shortName}
                  onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                  placeholder="e.g. IT"
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%' }}>
              {editingId ? "Update Record" : "Commit Record"}
            </button>
          </form>
        )}

        <section className="master-table-card">
          <div className="table-action-header" style={{ flexWrap: 'wrap', gap: '1rem' }}>
            <h3>Registry Output ({filteredDepartments.length})</h3>
            <input
              type="text"
              placeholder="🔍 Search department name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '300px' }}
            />
          </div>
          <div className="table-wrapper">
            <table className="standard-table">
              <thead>
                <tr>
                  <th>Identity</th>
                  <th>Core ID</th>
                  <th style={{ textAlign: 'right' }}>Management</th>
                </tr>
              </thead>
              <tbody>
                {filteredDepartments.map((dept) => (
                  <tr key={dept._id}>
                    <td style={{ fontWeight: 700 }}>{dept.name}</td>
                    <td><span className="status-tag tag-progress">{dept.shortName}</span></td>
                    <td>
                      <div className="action-btn-group">
                        <button className="btn-icon-only" onClick={() => handleEdit(dept)} title="Edit">✎</button>
                        <button className="btn-icon-only btn-delete-icon" onClick={() => setDeleteTargetId(dept._id)} title="Delete">✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredDepartments.length === 0 && (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '3rem', opacity: 0.6 }}>
                      No department records matching query.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ConfirmModal
          isOpen={!!deleteTargetId}
          title="Delete Department?"
          message="Are you sure you want to permanently remove this department record?"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTargetId(null)}
        />
      </div>
    </div>
  );
};

export default DepartmentPage;
